import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Shell, Section, KpiCard, StatusPill } from "@/components/loomi/Layout";
import { ADMIN_NAV } from "./AdminDashboard";
import { api, formatApiError } from "@/lib/api";
import { CopyField } from "@/components/loomi/CopyField";
import QRCode from "react-qr-code";
import { toast } from "sonner";

export default function AdminProjectDetail() {
  const { pid } = useParams();
  const [p, setP] = useState(null);
  const [secret, setSecret] = useState(null);
  const [logs, setLogs] = useState([]);
  const [sessions, setSessions] = useState([]);

  const load = async () => {
    const [pj, lg, ss] = await Promise.all([
      api.get(`/projects/${pid}`),
      api.get(`/projects/${pid}/logs`),
      api.get(`/projects/${pid}/sessions`),
    ]);
    setP(pj.data); setLogs(lg.data); setSessions(ss.data);
  };
  const loadSecret = async () => {
    const { data } = await api.get(`/projects/${pid}/secret`); setSecret(data);
  };
  useEffect(() => { load(); }, [pid]);

  const setStatus = async (status) => {
    try {
      await api.patch(`/projects/${pid}/status`, { status });
      toast.success(`Project ${status}`);
      load();
    } catch (e) { toast.error("Failed", { description: formatApiError(e) }); }
  };

  if (!p) return <Shell title="Loading project…" portalLabel="Admin" nav={ADMIN_NAV}><div className="text-mute" data-testid="project-loading">Loading…</div></Shell>;

  const c = p.counters || {};
  return (
    <Shell title={p.name} portalLabel="Admin" nav={ADMIN_NAV}>
      <div className="flex items-center gap-3 mb-6">
        <StatusPill status={p.status} />
        <span className="text-xs uppercase tracking-widest text-mute" data-testid="detail-type">{p.type} project</span>
        <span className="text-xs text-mute font-mono">{p.id}</span>
        <div className="ml-auto flex gap-2">
          {["live","paused","closed"].map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`set-status-${s}`}
              className={`text-xs px-3 py-1.5 rounded-md border ${p.status===s ? "bg-indigo-500/20 border-indigo-400/50 text-indigo-200" : "bg-white/5 border-white/10 hover:bg-white/10"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <KpiCard label="Starts" value={c.starts || 0} accent="blue" testId="pd-starts" />
        <KpiCard label="Complete" value={c.complete || 0} accent="green" testId="pd-complete" />
        <KpiCard label="Terminate" value={c.terminate || 0} accent="red" testId="pd-terminate" />
        <KpiCard label="Overquota" value={c.overquota || 0} accent="amber" testId="pd-overquota" />
        <KpiCard label="Quality-T" value={c.quality_term || 0} accent="red" testId="pd-quality" />
        <KpiCard label="Security-T" value={c.security_term || 0} accent="red" testId="pd-security" />
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-8">
        <Section title="Redirect URLs" testId="section-urls">
          {p.type === "adhoc" ? (
            <div className="space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-mute mb-1">Short-code entry</div>
                <CopyField value={p.urls.entry} label="Entry URL" testId="url-entry" />
              </div>
              <div className="flex items-center gap-4">
                <div className="bg-white p-3 rounded-md" data-testid="qr-code"><QRCode value={p.urls.entry} size={110} /></div>
                <div className="text-sm text-mute">Share this short link via WhatsApp, SMS or print. Every hit is signed, logged, and idempotent.</div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <UrlRow label="Entry" v={p.urls.entry} tid="url-entry" />
              <UrlRow label="Complete" v={p.urls.complete} tid="url-complete" />
              <UrlRow label="Terminate" v={p.urls.terminate} tid="url-terminate" />
              <UrlRow label="Overquota" v={p.urls.overquota} tid="url-overquota" />
              <UrlRow label="Quality-term" v={p.urls.quality_term} tid="url-quality" />
              <UrlRow label="Security-term" v={p.urls.security_term} tid="url-security" />
              <div className="pt-2">
                {!secret ? (
                  <button onClick={loadSecret} data-testid="reveal-secret-btn" className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-md px-3 py-2">Reveal signing secret & example token</button>
                ) : (
                  <div className="space-y-2">
                    <div className="text-[10px] uppercase tracking-widest text-mute">Signing secret (HMAC-SHA256)</div>
                    <CopyField value={secret.secret} testId="url-secret" />
                    <div className="text-[10px] uppercase tracking-widest text-mute mt-2">Example token for rid={secret.example_rid}, status=complete</div>
                    <CopyField value={secret.example_token} testId="url-example-token" />
                  </div>
                )}
              </div>
            </div>
          )}
        </Section>

        <Section title="Config" testId="section-config">
          <dl className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
            <Meta k="Target N" v={p.target_n} />
            <Meta k="CPI" v={`$${(p.cpi || 0).toFixed(2)}`} />
            <Meta k="LOI" v={`${p.loi} min`} />
            <Meta k="Incidence" v={`${p.incidence_rate}%`} />
            <Meta k="Reward" v={`${p.reward_points} pts`} />
            <Meta k="Survey URL" v={p.survey_url} mono wide />
          </dl>
        </Section>
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <Section title="Recent redirect logs" testId="section-logs">
          <LogsTable logs={logs} />
        </Section>
        <Section title="Recent sessions" testId="section-sessions">
          <SessionsTable sessions={sessions} />
        </Section>
      </div>

      <div className="mt-6 text-xs text-mute">
        <Link to="/admin/projects" className="hover:text-white" data-testid="back-to-projects">← back to projects</Link>
      </div>
    </Shell>
  );
}

function UrlRow({ label, v, tid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-mute mb-1">{label}</div>
      <CopyField value={v} testId={tid} />
    </div>
  );
}
function Meta({ k, v, wide, mono }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-mute">{k}</div>
      <div className={`text-sm ${mono ? "font-mono break-all" : ""}`}>{v}</div>
    </div>
  );
}
export function LogsTable({ logs }) {
  if (!logs?.length) return <div className="text-mute text-sm py-6 text-center" data-testid="logs-empty">No redirect events yet.</div>;
  return (
    <div className="overflow-x-auto max-h-[380px] overflow-y-auto">
      <table className="w-full text-xs" data-testid="logs-table">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-mute border-b border-white/10 sticky top-0 bg-black/60 backdrop-blur">
            <th className="py-2 pr-3">Time</th><th className="py-2 pr-3">Event</th><th className="py-2 pr-3">Valid</th><th className="py-2 pr-3">IP</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((l) => (
            <tr key={l.id} className="border-b border-white/5 hover:bg-white/5">
              <td className="py-2 pr-3 font-mono text-mute">{new Date(l.timestamp).toLocaleTimeString()}</td>
              <td className="py-2 pr-3 font-mono">{l.event_type}</td>
              <td className={`py-2 pr-3 ${l.token_valid ? "status-complete" : "status-terminate"}`}>{l.token_valid ? "ok" : "bad"}</td>
              <td className="py-2 pr-3 font-mono text-mute">{l.ip}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
export function SessionsTable({ sessions }) {
  if (!sessions?.length) return <div className="text-mute text-sm py-6 text-center" data-testid="sessions-empty">No sessions yet.</div>;
  return (
    <div className="overflow-x-auto max-h-[380px] overflow-y-auto">
      <table className="w-full text-xs" data-testid="sessions-table">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-mute border-b border-white/10 sticky top-0 bg-black/60 backdrop-blur">
            <th className="py-2 pr-3">RID</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Entry</th><th className="py-2 pr-3">Exit</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((s) => (
            <tr key={s.id} className="border-b border-white/5 hover:bg-white/5">
              <td className="py-2 pr-3 font-mono">{s.rid_token}</td>
              <td className="py-2 pr-3"><StatusPill status={s.status} /></td>
              <td className="py-2 pr-3 font-mono text-mute">{s.entry_time ? new Date(s.entry_time).toLocaleTimeString() : "—"}</td>
              <td className="py-2 pr-3 font-mono text-mute">{s.exit_time ? new Date(s.exit_time).toLocaleTimeString() : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
